import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { take, tap } from 'rxjs';

import { OrderService } from './order.service';
import { LocalStorageService } from './local-storage.service';
import { ModalContentService } from './modal-content/modal-content.service';
import { ProductCardV2Service } from './product-card-v2/product-card-v2.service';
import { FirebaseCrudService } from './firebase-crud.service';
import { DataLayerService } from './data-layer/data-layer.service';
import { KlaviyoService } from './klaviyo/klaviyo.service';
import { KLAVIYOEVENTS } from './klaviyo/events';
import { SignalsStoreService } from './signals-store.service';
import { NotificationService } from './notification/notification.service';
import { ProductsService } from '../product/products.service';
import {
  isAbandonedUser,
  isAddressUpdateRequired,
  isNoAddressUser,
  isANoPaymentMethodUser,
  trackAddToCartEvent,
  arrayToMapMultiKey,
} from './common/utils';
import { updateFirebaseOrderAndSubTrigger } from './common/order.utils';
import { UpdateOrderParams } from './types/order.type';
import { ProductType } from './types/common.enums';
import { SearchSuggestion } from './typesense-search.service';

/** Result of an {@link QuickAddService.addOrNavigate} attempt. */
export type QuickAddResult = 'added' | 'navigated' | 'blocked';

/**
 * Shared quick-add flow for the compact product cards used in search and the
 * promotions modal (DEV-9447). Extracted verbatim from
 * `search-bar.component.ts#onProductAddToCart` so both surfaces add the exact
 * variant with the same validations, tracking and PDP fallback — without the
 * promotions modal having to re-implement (or the search bar having to change).
 */
@Injectable({ providedIn: 'root' })
export class QuickAddService {
  private router = inject(Router);
  private productService = inject(ProductsService);
  private orderService = inject(OrderService);
  private localStorageService = inject(LocalStorageService);
  private modalContentService = inject(ModalContentService);
  private activeModal = inject(NgbModal);
  private productCardV2Service = inject(ProductCardV2Service);
  private firebaseCrudService = inject(FirebaseCrudService);
  private dataLayerService = inject(DataLayerService);
  private klaviyoService = inject(KlaviyoService);
  private signalsStoreService = inject(SignalsStoreService);
  private notificationService = inject(NotificationService);

  /** Expanded-card id currently being added (drives per-card spinners). */
  readonly addingProductId = signal<string | null>(null);

  get hasSession(): boolean {
    return !!this.signalsStoreService.hasSession();
  }

  /**
   * Quick-adds the exact variant a compact card represents, or navigates to the
   * PDP when quick-add isn't possible (anonymous user, needs variant selection,
   * or out of stock). `beforeNavigate` lets the caller close its modal first.
   */
  async addOrNavigate(
    product: SearchSuggestion,
    opts: { beforeNavigate?: () => void } = {},
  ): Promise<QuickAddResult> {
    // Route to PDP when the user is anonymous, the product needs variant
    // selection (canQuickAdd=false on the indexed doc) or it's OOS upfront.
    if (!this.hasSession || !product.canQuickAdd || product.stock?.isOut) {
      this.#navigate(product, opts);
      return 'navigated';
    }

    // Validation gates (mirror the search-bar flow).
    if (isAbandonedUser(this.localStorageService, this.modalContentService, this.activeModal)) return 'blocked';
    if (isAddressUpdateRequired(this.localStorageService, this.modalContentService, this.activeModal, this.router)) return 'blocked';
    if (isNoAddressUser(this.localStorageService, this.modalContentService, this.activeModal, this.router)) return 'blocked';
    if (isANoPaymentMethodUser(this.localStorageService, this.modalContentService, this.activeModal)) return 'blocked';

    const hasSkipped = await this.productCardV2Service.hasSkippedOrder();
    if (hasSkipped) return 'blocked';

    const productId = parseInt(product.parentProductId || product.id, 10);
    this.addingProductId.set(product.id);

    return new Promise<QuickAddResult>((resolve) => {
      this.productService
        .getProductsDetailsByIds([productId])
        .pipe(
          take(1),
          tap((products: any) => {
            const fullProduct = Array.isArray(products) ? products[0] : products[productId];
            if (!fullProduct) {
              this.notificationService.show({ text: 'Could not load product details.', type: 'error' });
              this.addingProductId.set(null);
              return resolve('blocked');
            }

            const stockMap = arrayToMapMultiKey(
              ['productId', 'attribute.id', 'attribute.value.id'],
              fullProduct.stockInfo || [],
            );
            const selectedAttribute =
              product.attributeId && product.attributeValueId
                ? { id: product.attributeId, value: { id: product.attributeValueId } }
                : null;
            const variant = this.productCardV2Service.getVariant(stockMap, fullProduct.id, selectedAttribute);

            if (!variant) {
              // OOS at click time — surface alternatives on the PDP.
              this.notificationService.show({
                text: `${fullProduct.name.trim()} is sold out — choose an alternative on the product page.`,
                type: 'info',
                delay: 3500,
              });
              this.addingProductId.set(null);
              this.#navigate(product, opts);
              return resolve('navigated');
            }

            const data: UpdateOrderParams = {
              product: {
                ...fullProduct,
                variantId: variant.id,
                quantity: 1,
                packageId: product.packageId || null,
              },
              type: ProductType.common,
              orderId: this.orderService.order()?.id || null,
            };

            this.orderService
              .updateOrder(data, false, false)
              .pipe(take(1))
              .subscribe((res: any) => {
                if (
                  res.data?.order?.outOfStockProducts?.length ||
                  res.data?.outOfStockProducts?.length ||
                  res.data?.order?.notAvailableProducts?.length ||
                  res.data?.notAvailableProducts?.length
                )
                  return;
                const variantLabel = product.variantLabel ? ` (${product.variantLabel})` : '';
                this.notificationService.show({
                  text: `${fullProduct.name.trim()}${variantLabel} added to order!`,
                  type: 'success',
                  delay: 3000,
                });

                updateFirebaseOrderAndSubTrigger(this.signalsStoreService, this.firebaseCrudService);
                this.klaviyoService.trackEvent(
                  KLAVIYOEVENTS.ProductAddedToCart,
                  this.orderService.setupProductForKlaviyo(fullProduct),
                );
                trackAddToCartEvent(this.dataLayerService, { ...fullProduct, quantity: 1 });
              });

            this.addingProductId.set(null);
            resolve('added');
          }),
        )
        .subscribe({
          error: () => {
            this.addingProductId.set(null);
            resolve('blocked');
          },
        });
    });
  }

  #navigate(product: SearchSuggestion, opts: { beforeNavigate?: () => void }): void {
    if (!product?.productUrl) return;
    opts.beforeNavigate?.();
    this.router.navigateByUrl(`/shop/${product.productUrl}`);
  }
}
